import React, { Component } from "react";
import { RoutedViewProps } from "@my-dld/utils/UrlUtils";
import PageContent from "@my-dld/components/PageContent";
import Strings from "@my-dld/assets/strings";
import PageTitle from "@my-dld/components/PageTitle";
import { DashboardBCItem } from "@my-dld/contexts/PageContext";
import ListDataView from "@my-dld/components/DataView/ListDataView";
import { JOPServiceIds } from "./index";
import { showErrorMessage } from "@my-dld/utils/UIUtils";
import { OwnerCommitteeApi } from "../../ownercommitteeapi";
import { TabuProfile } from "@10x/sso/TabuProfile";
import { OwnerCommitteeLookup, ServiceRate } from "../../ownercommitteeapi/entities";
import { LocaleUtil, ServerDate } from "@eres/core";
import { navigate } from "@my-dld/utils/StoreUtils";
import JOPRequestDetailsPopup from "./JOPRequestDetailsPopup";
import { JQModal } from "@my-dld/components/Popup/Utils";
import CardNoData from "@my-dld/components/CardNoData";
import { formatAppDate } from "@my-dld/utils/Utils";
import SuccessPopup from "./SuccessPopup";
import WorkflowPopup from "./WorkflowPopup";
import { Rating } from "react-simple-star-rating";
import { JOPApi } from "./Api";
import { NameSet } from "../../apis/privatesell/enitites";

interface Props extends RoutedViewProps<{ id: JOPServiceIds; requestId?: number }> {
	detailView?: boolean;
}

interface IJOPRequest {
	jobServiceId: number;
	referenceNumber: string;
	propertyNumber: string;
	basedOn?: any;
	status: OwnerCommitteeLookup;
	createdDate: ServerDate;
	canCloseRequest: boolean;
	canRequestTender: boolean;
	forwardToOC: boolean;
	requestType?: any;
	deadlLine: ServerDate;
	jopServiceEntries?: IJOPDetailServiceEntry[];
}

export interface IJOPDetailServiceEntry {
	id: number;
	companyStatus: OwnerCommitteeLookup;
	ownerStatus: OwnerCommitteeLookup;
	ownerFeedback?: string;
	companyFeedback?: string;
	canSubmitOwnerFeedback: boolean;
	documents: { id: number; url: string }[];
	category: OwnerCommitteeLookup;
	subCategory?: OwnerCommitteeLookup;
	description: string;
	meetingDate?: string;
	serviceRate?: ServiceRate;
}

export interface IRVSComplaint {
	transactionNumber?: string;
	transactionDate?: string;
	transactionStatus?: NameSet;
	transactionRemarks?: string;
	closeReasonName?: NameSet;
}

export interface IJOPRequestDetails {
	jobServiceId: number;
	referenceNumber: string;
	basedOn?: any;
	status: OwnerCommitteeLookup;
	createdDate: ServerDate;
	canCloseRequest: boolean;
	canRequestTender: boolean;
	forwardToOC: boolean;
	requestType: OwnerCommitteeLookup;
	deadlLine: string;
	jopServiceEntries: IJOPDetailServiceEntry[];
	propertyType: OwnerCommitteeLookup;
	propertyNumber: string;
}

interface State {
	requests?: IJOPRequest[];
	request?: IJOPRequestDetails;
	details?: IJOPDetailServiceEntry;
	transaction?: IRVSComplaint;
	isLoading?: boolean;
	submitOwnerFeedback?: boolean;
	confirmMessage?: string;
	workflowType?: "closeRequest" | "requestTender" | "forwardToOc";
}

class JOPRequests extends Component<Props, State> {

	constructor( props: Props ) {
		super( props );
		this.state = { requests: [] };
	}

	componentDidMount() {
		if ( this.props.detailView ) {
			this.getDetails();
		} else {
			this.getRequests();
		}
	}

	componentDidUpdate( prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any ) {
		if ( prevProps.detailView !== this.props.detailView ) this.componentDidMount();
	}

	getTitle = () => {
		switch ( Number( this.props.match.params.id ) ) {
			case JOPServiceIds.COMPLAINT:
				return Strings.complaint_requests;
			case JOPServiceIds.ENQUIRY:
				return Strings.enquiry_requests;
			case JOPServiceIds.LEGAL:
				return Strings.legal_requests;
			case JOPServiceIds.SUGGESTION:
				return Strings.suggestion_requests;
		}
	};

	getTitleExtra = () => {
		const request = this.state.request;
		if ( !this.props.detailView || !request ) return "";
		if ( request.propertyType.nameEn?.toLowerCase() === "unit" ) {
			return " - " + Strings.Unit_Number_Label + " : " + request.propertyNumber;
		} else {
			return " - " + Strings.villa_name + " : " + request.propertyNumber;
		}
	};

	getType = () => {
		return Number( this.props.match.params.id ) as JOPServiceIds;
	};

	getModalTitle = () => {
		if ( !this.state.submitOwnerFeedback ) {
			switch ( this.getType() ) {
				case JOPServiceIds.COMPLAINT:
					return Strings.complaint_details;
				case JOPServiceIds.ENQUIRY:
					return Strings.enquiry_details;
				case JOPServiceIds.LEGAL:
					return Strings.legal_request_details;
				case JOPServiceIds.SUGGESTION:
					return Strings.suggestion_details;
			}
		} else {
			return Strings.submit_feedback;
		}
	};

	render() {
		const { detailView } = this.props;
		const requests = detailView ? this.state.request?.jopServiceEntries : this.state.requests?.sort( ( a, b ) => {
			return b.createdDate.asDate().getTime() - a.createdDate.asDate().getTime();
		} );
		const request = this.state.request;
		const transaction = this.state.transaction;
		const title = this.getTitle();
		const titleExtra = this.getTitleExtra();
		const details = this.state.details;
		const isLoading = this.state.isLoading;
		const dataAvailable = requests && requests.length !== 0;
		return <PageContent
			title={ title + titleExtra }
			hideQLinks={ true }>
			<PageTitle title={ Strings.jop_services } paths={ [ DashboardBCItem ] }/>
			{ isLoading ? <span className="spinner-border" role="status">
                 <span className="sr-only">{ Strings.loading }</span>
             </span> : <>
				{ !dataAvailable ? <CardNoData containerClass="alert alert-warning"/> :
					<>
						<ListDataView
							renderListHeader={ this.renderListHeader }
							renderListItem={ this.renderListItem }
							isSearchable={ false }
							id={ "JOPRequests" }
							items={ requests ?? [] as any }/>

						{ detailView && request ? <>
							{ request.canRequestTender ? <div className={ "alert alert-warning" }>{ Strings.jop_note }</div> : null }
							<div>
								{ request?.canCloseRequest ? <button
									onClick={ this.onCloseRequest }
									className={ "btn btn_1 btn_style1 me-2" }>{ Strings.close_request }</button> : null }
								{ request?.forwardToOC ? <button
									onClick={ this.onForwardToOc }
									className={ "btn btn_style1 btn-grey me-2" }>{ Strings.forward_to_oc }</button> : null }
								{ request?.canRequestTender ? <button
									className={ "btn btn_style1 btn-grey" }
									onClick={ this.onRequestTender }>{ Strings.transfer_to_rera }</button> : null }
							</div>
							{ details ?
								<JOPRequestDetailsPopup
									title={ this.getModalTitle() }
									submitOwnerFeedback={ this.state.submitOwnerFeedback }
									request={ request } details={ details } transaction={transaction} onDismiss={ this.onDismissDetailsPopup }
									onShowSuccess={ this.onShowSuccess }/> : null }
							{ this.state.workflowType ? <WorkflowPopup
								onShowSuccess={ this.onShowSuccess }
								workflowType={ this.state.workflowType } onDismiss={ this.onDismissWorkflowPopup }
								request={ this.state.request! }/> : null }
						</> : null }
					</>
				}
			</> }
			<SuccessPopup onDismiss={ this.onDismissSuccessPop }/>
		</PageContent>;
	}

	onDismissWorkflowPopup = () => {
		JQModal( "JOPServiceWorkflowPopup" ).hide();
		this.setState( { workflowType: undefined } );
	};

	onRequestTender = () => {
		this.setState( { workflowType: "requestTender" }, () => JQModal( "JOPServiceWorkflowPopup" ).show() );
	};

	onForwardToOc = () => {
		this.setState( { workflowType: "forwardToOc" }, () => JQModal( "JOPServiceWorkflowPopup" ).show() );
	};

	onCloseRequest = () => {
		this.setState( { workflowType: "closeRequest" }, () => JQModal( "JOPServiceWorkflowPopup" ).show() );
	};

	onShowSuccess = () => {
		JQModal( "JOPServiceSuccessPopup" ).show();
	};

	onDismissSuccessPop = () => {
		JQModal( "JOPServiceSuccessPopup" ).hide();
		window.location.reload();
	};

	onDismissDetailsPopup = () => {
		JQModal( "JOPDetailServiceEntry" ).hide();
	};

	hasRating = () => {
		return this.state.request?.jopServiceEntries.some( e => e.serviceRate != null );
	};

	renderListHeader = () => {
		if ( this.props.detailView ) {
			return <tr>
				<th>#</th>
				<th>{ Strings.request_number }</th>
				<th>{ Strings.category }</th>
				{ this.getType() === JOPServiceIds.LEGAL ? <th>{ Strings.sub_category }</th> : null }
				<th>{ Strings.request_date }</th>
				<th>{ Strings.management_company_status }</th>
				<th>{ Strings.requester_status }</th>
				{ this.hasRating() ? <th>{ Strings.rating }</th> : null }
                <th>{Strings.updates}</th>
                <th>{ Strings.action }</th>
			</tr>;
		}
		return <tr>
			<th>#</th>
			<th>{ Strings.request_number }</th>
			<th>{ Strings.request_date }</th>
			<th>{ Strings.property_number }</th>
			<th>{ Strings.status }</th>
			<th>{ Strings.action }</th>
		</tr>;
	};

	getRequests = () => {
		this.setIsLoading( true );
		JOPApi.getJOPRequests( TabuProfile.getParticipantNumber()!, this.getType() )
		.then( r => {
			if ( r && r.validationErrorsList && r.validationErrorsList.length > 0 ) {
				showErrorMessage( OwnerCommitteeApi.getValidationError( r.validationErrorsList ) );
			} else if ( r && r.response && r.response.jopServices && r.response.jopServices.length > 0 ) {
				this.setState( { requests: r.response.jopServices ?? [], request: undefined } );
			}
			this.setIsLoading( false );
		} )
		.catch( reason => {
			this.setIsLoading( false );
			showErrorMessage( reason );
		} );
	};

	setIsLoading = ( loading: boolean ) => {
		this.setState( { isLoading: loading } );
	};

	getDetails = () => {
		this.setIsLoading( true );
		const requestId = Number( this.props.match.params.requestId );
		JOPApi.getJOPRequest( requestId )
		.then( r => {
			if ( r && r.validationErrorsList && r.validationErrorsList.length > 0 ) {
				showErrorMessage( OwnerCommitteeApi.getValidationError( r.validationErrorsList ) );
			} else if ( r && r.response ) {
				this.setState( { request: r.response.jopService, requests: [] , transaction: r.response.rvsComplaint } );
			}
			this.setIsLoading( false );
		} )
		.catch( e => {
			this.setIsLoading( false );
			showErrorMessage( e );
		} );
	};

	onViewDetails = ( item: IJOPRequest ) => {
		navigate( `/mydld/jop/requests/${ this.props.match.params.id }/${ item.jobServiceId }` );
	};

	onViewDetailsPopup = ( item: IJOPDetailServiceEntry ) => {
		this.setState( { details: item }, () => {
			JQModal( "JOPDetailServiceEntry" ).show();
		} );
	};

	renderListItem = ( item: IJOPRequest | IJOPDetailServiceEntry, index: number ) => {
		if ( this.props.detailView ) {
			const request = this.state.request;
			const i = item as IJOPDetailServiceEntry;
			return <tr data-item={ JSON.stringify( item ) }>
				<td data-label="#">{ index + 1 }</td>
				<td data-label={ Strings.request_number }>{ request?.referenceNumber }</td>
				<td data-label={ Strings.category }>{ LocaleUtil.getLocalValue( i.category ) }</td>
				{ this.getType() === JOPServiceIds.LEGAL ?
					<td data-label={ Strings.sub_category }>{ LocaleUtil.getLocalValue( i.subCategory ) }</td> : null }
				<td data-label={ Strings.request_date }>{ formatAppDate( request?.createdDate! ) }</td>
				<td data-label={ Strings.management_company_status }>{ LocaleUtil.getLocalValue( i.companyStatus ) }</td>
				<td data-label={ Strings.owner_status }>{ LocaleUtil.getLocalValue( i.ownerStatus ) }</td>
				{ this.hasRating() && i.serviceRate != null ? <td data-label={ Strings.rating }>
					<Rating initialValue={ i.serviceRate } size={ 20 } readonly={ true }/>
				</td> : <td data-label={ Strings.rating }>{ Strings.not_available }</td> }
				<td data-label={ Strings.action }>
					<button
						onClick={ () => {
							this.setState( { submitOwnerFeedback: false }, () => {
								this.onViewDetailsPopup( i );
							} );
						} }
						type="button" className="btn underline p-0 font-size14">
						{ Strings.view }
					</button>
					{ i.canSubmitOwnerFeedback ? <>
						{ " / " }
						<button
							onClick={ () => {
								this.setState( { submitOwnerFeedback: true }, () => {
									this.onViewDetailsPopup( i );
								} );
							} }
							type="button" className="btn underline p-0 font-size14">
							{ Strings.edit }
						</button>
					</> : null }
				</td>
			</tr>;
		} else {
			const i = item as IJOPRequest;
			return <tr data-item={ JSON.stringify( i ) }>
				<td data-label="#">{ index + 1 }</td>
				<td data-label={ Strings.request_number }>{ i.referenceNumber }</td>
				<td data-label={ Strings.request_date }>{ formatAppDate( i.createdDate.asDate() ) }</td>
				<td data-label={ Strings.property_number }>{ i.propertyNumber }</td>
				<td data-label={ Strings.status }>{ LocaleUtil.getLocalValue( i.status ) }</td>
				<td data-label={ Strings.action } className="text-start">
					<button
						type="button" className="btn underline p-0 font-size14" onClick={ () => this.onViewDetails( i ) }>
						{ Strings.view_details }
					</button>
				</td>
			</tr>;
		}
	};
}

export default JOPRequests;
